import { HttpClient } from '@angular/common/http';
import { Data, Response, UPSERT, waitAndRetry } from '@dimandzhi/graphql';
import { currentMethodName } from 'app/current-method-name';
import { GraphQL } from 'app/graphql';
import { MediaType } from 'app/m/scheduler/internal-schedule-class';
import { environment as env } from 'environments/environment';
import _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const gqlType = GraphQL.Schedule;
type gqlType = GraphQL.Schedule;
const ModelName = gqlType.getModelName();
export class Schedules {
  private schedulesFirstCall: boolean;
  private schedules: BehaviorSubject<gqlType[]>;

  constructor(
    protected http: HttpClient
  ) { }

  initSchedules() {
    this.schedulesFirstCall = true;
    this.schedules = new BehaviorSubject<gqlType[]>([]);
  }

  setSchedule(schedule: gqlType) {
    let action: UPSERT = 'update';
    if (schedule.id === undefined) {
      action = 'insert';
    }
    return this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      mutation {
        ${action} {
          Schedule(values:[{${schedule.mutationData()}}])
        }
      }`
    ).pipe(
      Data.fromResp,
      map(data => {
        const result = data.upsertResp(action, ModelName);
        if (result === undefined) {
          throw new Error(`result is undefined`);
        }
        return result;
      })
    );
  }

  insertSchedules(schedule: gqlType) {
    return this.setSchedule(schedule).pipe(
      map(IDs => {
        if (!_.isArrayLike(IDs)) {
          throw new Error(`IDs is not an array: ${typeof IDs} ${IDs}`);
        }
        const id = _.head(IDs);
        if (!_.isNumber(id)) {
          throw new Error(`id is not a number: ${typeof id} ${id}`);
        }
        schedule.id = id;
        this.schedules.next(this.schedules.value.concat(schedule));
        return id;
      })
    );
  }

  deleteSchedule(schedule: gqlType) {
    this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      mutation {
        ${ModelName} {
          delete(values:[${schedule.id}])
        }
      }`
    ).subscribe(data => {
      if (!data.errors) {
        // or make local changes?
        this.refreshSchedules();
      } else {
        throw new Error('something wrong');
      }
    });
  }

  querySchedulesForChannel(channelID: number) {
    return this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      query {
        ${ModelName} {
          get(channelID: ${channelID}) {
            data {
              ${gqlType.fields({ withSubFields: true })}
            }
          }
        }
      }`
    ).pipe(
      waitAndRetry,
      Data.fromResp,
      map(data => gqlType.fromData(data))
    );
  }

  getSchedules(): Observable<gqlType[]>;
  getSchedules(asSnapshot: boolean): gqlType[];
  getSchedules(asSnapshot?: boolean) {
    if (this.schedulesFirstCall) {
      this.refreshSchedules();
      this.schedulesFirstCall = false;
    }
    if (asSnapshot) {
      return this.schedules.getValue();
    }
    return this.schedules.asObservable();
  }

  refreshSchedules(): void
  refreshSchedules(ids: number[]): void
  refreshSchedules(ids?: number[]): void {
    if (ids !== undefined && _.every(ids, _.isNumber)) {
      this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
        query {
          ${ModelName} {
            filter(id:[${ids.join(',')}]) {
              data {
                ${gqlType.fields({ withSubFields: true })}
              }
            }
          }
        }`
      ).pipe(
        waitAndRetry,
        Data.fromResp,
        map(data =>
          data.get<gqlType>(gqlType, 'filter.data')
        )
      ).subscribe({
        next: newSchedules => {
          let schedules = this.schedules.getValue();
          _.remove(schedules, s => _.includes(ids, s.id));
          schedules = schedules.concat(...newSchedules);
          this.schedules.next(schedules);
        },
        error: err => this.schedules.error(err)
      });
      return;
    }
    this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      query {
        ${ModelName} {
          get {
            data {
              ${gqlType.fields({ withSubFields: true })}
            }
          }
        }
      }`
    ).pipe(
      waitAndRetry,
      Data.fromResp
    ).subscribe({
      next: data =>
        this.schedules.next(gqlType.fromData(data)),
      error: err => this.schedules.error(err)
    });
  }

  viewMergedSchedule
    <
      ThisTimeWindow extends GraphQL.TimeWindow,
      ThisMediaType extends MediaType,
      R = mergedScheduleForTimeWindowClass<ThisTimeWindow, ThisMediaType>
    >(
      timeWindow: ThisTimeWindow,
      mediaType: ThisMediaType,
      timeWindowArg: mergedScheduleArgumemntType<ThisTimeWindow>,
      days?: number,
      mediaID?: number,
    ): Observable<R[]> {
    const fieldName = graphqlFieldName(timeWindow, mediaType);
    const timeWindowArgString = {
      [GraphQL.TimeWindow.ForDate]: `date: "${timeWindowArg}", timezone: "${moment().tz()}"`,
      [GraphQL.TimeWindow.ForPeriod]: `lastHours: ${timeWindowArg}`
    };
    if (_.isNumber(days) && !_.isNaN(days)) {
      timeWindowArgString[GraphQL.TimeWindow.ForDate] += `, days: ${days}`
    }
    let args = [`${timeWindowArgString[timeWindow]}`];
    if (_.isNumber(mediaID) && !_.isNaN(mediaID)) {
      args.push(`${mediaType}: ${mediaID}`);
    }
    return this.http.post<Response>(
      `${env.graphql}/viewMergedSchedule`, `
      query {
        ${ModelName} {
          ${fieldName}(${args.join(', ')}) {
            ${graphqlFields(timeWindow, mediaType)}
          }
        }
      }`
    ).pipe(
      waitAndRetry,
      Data.fromResp,
      map(data => {
        let schedules: R[] = [];
        for (let schedule of _.get(data.get(), `${ModelName}.${fieldName}`) as R[]) {
          schedules.push(
            Object.assign(
              new (GraphQL.mergedScheduleClassConstructor(mediaType, timeWindow)),
              schedule
            )
          );
        }
        return schedules;
      })
    );
  }
}

function graphqlFields(timeWindow: GraphQL.TimeWindow, mediaType: MediaType) {
  const commonFields = [
    'start',
    'duration',
    'langModelID',
    'spleeter',
    'groupID'
  ];
  const timeWindowBasedField: Record<GraphQL.TimeWindow, string[]> = {
    [GraphQL.TimeWindow.ForPeriod]: [
      'asrPriority',
      'dynamicASRPriorityFromDate',
    ],
    [GraphQL.TimeWindow.ForDate]: []
  };
  return _.union(commonFields, timeWindowBasedField[timeWindow], [mediaType]).join("\n");
};

function graphqlFieldName(timeWindow: GraphQL.TimeWindow, mediaType: MediaType) {
  return 'view' + mediaType.charAt(0).toUpperCase() + mediaType.slice(1, -2) + timeWindow;
}

type mergedScheduleArgumemntType<ThisTimeWindow = GraphQL.TimeWindow> =
  ThisTimeWindow extends GraphQL.TimeWindow.ForPeriod ?
  number :
  ThisTimeWindow extends GraphQL.TimeWindow.ForDate ?
  string :
  never

type mergedScheduleForTimeWindowClass<ThisTimeWindow = GraphQL.TimeWindow, ThisMediaType = MediaType> =
  ThisTimeWindow extends GraphQL.TimeWindow.ForPeriod ? (
    ThisMediaType extends MediaType.Channel ?
    GraphQL.MergedChannelScheduleForPeriod :
    ThisMediaType extends MediaType.ChannelStream ?
    GraphQL.MergedChannelStreamScheduleForPeriod :
    never
  ) :
  ThisTimeWindow extends GraphQL.TimeWindow.ForDate ? (
    ThisMediaType extends MediaType.Channel ?
    GraphQL.MergedChannelScheduleForDate :
    ThisMediaType extends MediaType.ChannelStream ?
    GraphQL.MergedChannelStreamScheduleForDate :
    never
  ) :
  never;